import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import logo from '../assets/botibonga_logo.svg';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuthStatus, isAuthenticated, isLoading } = useAdminAuth();

  // Check for auth callback params
  useEffect(() => {
    const authSuccess = searchParams.get('auth_success');
    const authError = searchParams.get('auth_error');

    if (authSuccess === 'true') {
      // Refresh auth status after successful login
      checkAuthStatus().then(() => {
        navigate('/', { replace: true });
      });
    }

    if (authError) {
      // Error is shown via URL params
    }
  }, [searchParams, checkAuthStatus, navigate]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleGoogleLogin = () => {
    // Redirect to backend OAuth endpoint
    window.location.href = `${API_URL}/admin/auth/login`;
  };

  const authError = searchParams.get('auth_error');
  const errorMessage = searchParams.get('message');

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-accent/5 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
          <span className="text-text-secondary">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-accent/5 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img src={logo} alt="Botibonga" className="w-20 h-20" />
          </div>
          <h1 className="text-2xl font-bold text-text-primary mb-2">
            Botibonga Admin
          </h1>
          <p className="text-text-secondary">
            Sign in to access the admin dashboard
          </p>
        </div>

        {/* Login Card */}
        <div className="card p-8">
          {/* Error Message */}
          {authError && (
            <div className="mb-6 p-4 bg-danger/10 border border-danger/20 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-danger">
                    {authError === 'access_denied' ? 'Access Denied' : 'Authentication Failed'}
                  </p>
                  <p className="text-sm text-danger/80 mt-1">
                    {errorMessage || 'Only authorized admins can access this panel.'}
                  </p>
                </div>
              </div>
              {authError === 'access_denied' && (
                <div className="mt-3 pt-3 border-t border-danger/20">
                  <p className="text-xs text-text-muted">
                    If you need admin access, please contact the system administrator.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Google Sign-In Button */}
          <div className="flex flex-col items-center">
            <button
              onClick={handleGoogleLogin}
              className="flex items-center gap-3 px-6 py-3 bg-white border border-gray-300 rounded-lg shadow-sm hover:shadow-md hover:bg-gray-50 transition-all w-full justify-center"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span className="text-gray-700 font-medium">Sign in with Google</span>
            </button>
          </div>

          {/* Info */}
          <div className="mt-6 pt-6 border-t border-border">
            <p className="text-xs text-text-muted text-center">
              Only authorized admin accounts can access this panel.
              <br />
              Contact support if you need access.
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-text-muted mt-6">
          &copy; {new Date().getFullYear()} Botibonga. All rights reserved.
        </p>
      </div>
    </div>
  );
}
